<template>
    <template v-for="(specialPrize, prizeGroupIndex) in viewModel" :key="specialPrize.name">
        <h2 class="pp-lottery-winnings__title">{{ specialPrize.name }}</h2>
        <DsTable>
            <DsRow>
                <DsCell type="th" data-test="table-special-1-1">Kód sázky</DsCell>
                <DsCell type="th" data-test="table-special-2-1">Výhra</DsCell>
            </DsRow>
            <DsRow v-for="(prize, i) in specialPrize.prizePages[specialPrize.currentPage]" :key="i">
                <DsCell :data-test="`table-special-1-${i + 2}`">{{ prize.code }}</DsCell>
                <DsCell :data-test="`table-special-2-${i + 2}`"
                    ><strong>{{ prize.prize }}</strong></DsCell
                >
            </DsRow>
        </DsTable>
        <div v-if="specialPrize.totalPages > 1" class="bs-row">
            <div class="c-auto mx-auto">
                <Pagination
                    :total-pages="specialPrize.totalPages"
                    :current-page="specialPrize.currentPage"
                    :on-click-handler="(pageNumber) => pageChanged(prizeGroupIndex, pageNumber)"
                />
            </div>
        </div>
    </template>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import DsTable from "@sazka/design-system/src/table/ds-table.vue";
import DsRow from "@sazka/design-system/src/table-row/ds-table-row.vue";
import DsCell from "@sazka/design-system/src/table-cell/ds-table-cell.vue";
import Pagination from "@sazka/design-system/src/pagination/ds-pagination.vue";
import { LotteryDrawSpecialPrizeGroup } from "@/lotteries/services/model/lottery-draw-special-prize-group";
import { LotteryDrawSpecialPrize } from "@/lotteries/services/model/lottery-draw-special-prize";
import { useLotteryResults } from "@/lotteries/services/draw-info/pp-results";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { showAlert } from "@sazka/design-system/src/modal-alert/mount-alert";

const props = defineProps<{
    lottery: Lottery;
    specialPrizes: LotteryDrawSpecialPrizeGroup[];
}>();

interface ViewModelItem {
    name: string;
    prizePages: LotteryDrawSpecialPrize[][];
    totalPages: number;
    currentPage: number;
    type: "SFP" | "NFP";
}

const viewModel = ref<ViewModelItem[]>([]);

const resetViewModel = () => {
    viewModel.value = props.specialPrizes.map((specialPrize) => ({
        name: specialPrize.name,
        type: specialPrize.type,
        prizePages: [],
        totalPages: 1,
        currentPage: 1,
    }));
};

resetViewModel();

const { fetchSpecialPrize } = useLotteryResults();

const loadPage = async (prizeGroupIndex: number, pageNumber: number) => {
    const prizeType = viewModel.value[prizeGroupIndex].type;
    Loader.show();
    try {
        const data = await fetchSpecialPrize(props.lottery, prizeType, pageNumber);
        viewModel.value[prizeGroupIndex].prizePages[pageNumber] = data.data[0].prizes;
        viewModel.value[prizeGroupIndex].totalPages = data.pageCount;
    } catch (error) {
        showAlert({
            title: "Něco se pokazilo",
            subText: "Nepodařilo se načíst výhry, zkuste prosím obnovit stránku a zkusit to ještě jednou.",
        });
    } finally {
        Loader.hide();
    }
};

const pageChanged = async (prizeGroupIndex: number, pageNumber: number) => {
    await loadPage(prizeGroupIndex, pageNumber);
    viewModel.value[prizeGroupIndex].currentPage = pageNumber;
};

watch(
    () => props.specialPrizes,
    () => {
        resetViewModel();
        for (let i = 0; i < viewModel.value.length; i++) {
            loadPage(i, 1);
        }
    },
    {
        immediate: true,
    }
);
</script>
